import React, { useEffect, useMemo, useRef } from "react";
import rough from "roughjs/bundled/rough.esm";
import styled, { useTheme } from "styled-components";

// Faint hand-drawn scribbles scattered behind a section's content, like the
// stray pencil/chalk doodles kids leave around the edges of a chalkboard.

const STAR_PATH =
  "M20 4 L24 15 L36 15 L26 22 L30 34 L20 27 L10 34 L14 22 L4 15 L16 15 Z";
const SWIRL_PATH =
  "M4 22 C 4 10, 20 8, 21 19 C 22 28, 11 30, 11 22 C 11 17, 16 16, 18 20";
const HEART_PATH =
  "M20 33 C 6 22, 2 13, 9 8 C 14 4, 20 8, 20 14 C 20 8, 26 4, 31 8 C 38 13, 34 22, 20 33 Z";
const TRIANGLE_PATH = "M20 5 L36 33 L4 33 Z";
const CLOUD_PATH =
  "M8 26 C 2 26, 2 17, 9 17 C 9 10, 20 9, 22 15 C 30 12, 35 20, 29 24 C 33 30, 26 33, 22 30 C 18 34, 9 33, 8 26 Z";

const TYPES = [
  "star",
  "swirl",
  "circle",
  "zigzag",
  "plus",
  "line",
  "heart",
  "triangle",
  "cloud",
  "arrow",
];

const Layer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
`;

const DoodleWrap = styled.div`
  position: absolute;
  left: ${(p) => p.$x}%;
  top: ${(p) => p.$y}%;
  opacity: ${(p) => p.$opacity};
  transform: rotate(${(p) => p.$rotate}deg);
`;

const Doodle = ({ type, size, seed }) => {
  const svgRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const rc = rough.svg(svg);
    const opts = {
      stroke: theme.text_secondary,
      strokeWidth: 1.4,
      roughness: 2.6,
      seed,
    };
    let node;
    switch (type) {
      case "star":
        node = rc.path(STAR_PATH, opts);
        break;
      case "swirl":
        node = rc.path(SWIRL_PATH, opts);
        break;
      case "circle":
        node = rc.circle(20, 20, 24, opts);
        break;
      case "zigzag":
        node = rc.linearPath(
          [[2, 24], [12, 8], [20, 28], [28, 8], [38, 24]],
          opts
        );
        break;
      case "plus": {
        const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
        g.appendChild(rc.line(20, 6, 20, 34, opts));
        g.appendChild(rc.line(6, 20, 34, 20, opts));
        node = g;
        break;
      }
      case "heart":
        node = rc.path(HEART_PATH, opts);
        break;
      case "triangle":
        node = rc.path(TRIANGLE_PATH, opts);
        break;
      case "cloud":
        node = rc.path(CLOUD_PATH, opts);
        break;
      case "arrow": {
        const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
        g.appendChild(rc.line(4, 20, 34, 20, opts));
        g.appendChild(
          rc.linearPath([[24, 10], [34, 20], [24, 30]], opts)
        );
        node = g;
        break;
      }
      default:
        node = rc.line(4, 20, 36, 20, opts);
    }
    svg.appendChild(node);
  }, [type, seed, theme]);

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox="0 0 40 40"
      style={{ overflow: "visible", display: "block" }}
    />
  );
};

export const ChalkDoodles = ({ count = 8, seedBase = 1, minOpacity = 0.09, maxOpacity = 0.2 }) => {
  const items = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const type = TYPES[(seedBase * 3 + i * 7) % TYPES.length];
      arr.push({
        type,
        x: 4 + ((seedBase * 17 + i * 41) % 90),
        y: 6 + ((seedBase * 23 + i * 31) % 86),
        size: 22 + ((seedBase + i * 9) % 3) * 8,
        rotate: ((seedBase * 13 + i * 53) % 60) - 30,
        opacity:
          minOpacity +
          (((seedBase + i * 5) % 10) / 10) * (maxOpacity - minOpacity),
        seed: seedBase * 97 + i * 11 + 3,
      });
    }
    return arr;
  }, [count, seedBase, minOpacity, maxOpacity]);

  return (
    <Layer>
      {items.map((d, i) => (
        <DoodleWrap key={i} $x={d.x} $y={d.y} $rotate={d.rotate} $opacity={d.opacity}>
          <Doodle type={d.type} size={d.size} seed={d.seed} />
        </DoodleWrap>
      ))}
    </Layer>
  );
};
